// =============================================================================
// components/Safe.tsx — per-widget render-crash isolator.
//
// A tiny error boundary you wrap around a NON-CRITICAL widget so a render throw
// inside it can NEVER take down the whole app (the root ErrorBoundary would
// white-screen everything). On a caught error it logs (with a label for device
// console/diagnostics) and renders `fallback` (default: nothing).
//
//   <Safe label="StoryRow"><StoryRow .../></Safe>
//
// Use it for decorative / overlay / feed-header widgets. Do NOT use it to hide
// real bugs in core flows — it degrades gracefully AND surfaces the label.
// =============================================================================
import React from 'react';

interface Props {
  children: React.ReactNode;
  /** Shown instead of the children if they throw. Default: null (render nothing). */
  fallback?: React.ReactNode;
  /** Identifies the widget in the crash log so we know exactly what failed. */
  label?: string;
}
interface State { failed: boolean }

export default class Safe extends React.Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(
      `[CRYB] Safe caught in <${this.props.label ?? 'widget'}>:`,
      error?.message,
      info?.componentStack?.slice(0, 300),
    );
  }

  render() {
    if (this.state.failed) return <>{this.props.fallback ?? null}</>;
    return <>{this.props.children}</>;
  }
}
